import React, { FC, PropsWithChildren } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  LinearProgress,
  Typography,
  IconButton,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PauseIcon from "@mui/icons-material/Pause";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import VolumeOffIcon from "@mui/icons-material/VolumeOff";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import RecordVoiceOverIcon from "@mui/icons-material/RecordVoiceOver";
import VoiceOverOffIcon from "@mui/icons-material/VoiceOverOff";
import HourglassBottomIcon from "@mui/icons-material/HourglassBottom";
import PageWrapper from "@components/Home/PageWrapper";

export interface GameScreenShellProps {
  gameName: string;
  level: number;
  progress: number;
  feedback: string | null;
  timerSeconds: number | null;
  score: number | null;
  paused?: boolean;
  onBack: () => void;
  onPause?: () => void;
  /** When set, show "Game X of Y" (guided session) */
  gameIndex?: number;
  totalGames?: number;
  /** When set, show mute toggle (spec: global mute in session UI) */
  muted?: boolean;
  onMuteChange?: (muted: boolean) => void;
  /**
   * In-game voice-over toggle (Phase 1). When `onVoiceOverChange` is set,
   * render a button next to the mute control that flips the per-game
   * voice-over preference. The host game decides what `enabled` means
   * (renders the avatar overlay, triggers `speak()` on round events, etc.).
   * Hidden when `onVoiceOverChange` is undefined so games that don't
   * opt in keep their footer untouched.
   */
  voiceOverEnabled?: boolean;
  onVoiceOverChange?: (enabled: boolean) => void;
  /**
   * In-game "more time" control. Render only when caregiver has enabled the
   * feature for the patient. `disabled` should be set once the bonus has been
   * consumed for the current step (problem / trial / round).
   */
  moreTime?: {
    onRequest: () => void;
    disabled?: boolean;
  };
}

const GameScreenShell: FC<PropsWithChildren<GameScreenShellProps>> = ({
  gameName,
  level,
  progress,
  feedback,
  timerSeconds,
  score,
  paused = false,
  onBack,
  onPause,
  gameIndex,
  totalGames,
  muted = false,
  onMuteChange,
  voiceOverEnabled = false,
  onVoiceOverChange,
  moreTime,
  children,
}) => {
  const { t } = useTranslation();
  const formatTime = (s: number) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m}:${sec.toString().padStart(2, "0")}`;
  };
  const showGameOfTotal =
    gameIndex != null && totalGames != null && totalGames > 1;

  return (
    <PageWrapper>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
          minHeight: 0,
          height: "100%",
        }}
      >
      {/* Header: Back | Game Name | Level / Game X of Y */}
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        gap={1}
        mb={1}
      >
        <Button
          size="large"
          startIcon={<ArrowBackIcon />}
          onClick={onBack}
          sx={{ minHeight: 52 }}
        >
          {t("games.shell.back")}
        </Button>
        <Typography variant="h6" fontWeight={600} sx={{ fontSize: "1.1rem" }}>
          {gameName}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {showGameOfTotal
            ? t("games.shell.gameOfTotal", {
                current: gameIndex + 1,
                total: totalGames,
              })
            : t("games.shell.level", { level })}
        </Typography>
      </Box>

      {/* Progress bar */}
      <Box sx={{ mb: 2 }}>
        <LinearProgress
          variant="determinate"
          value={Math.min(100, progress * 100)}
          sx={{ height: 8, borderRadius: 1 }}
        />
      </Box>

      {/* Game + feedback + footer grouped and vertically centered */}
      <Box
        flex={1}
        display="flex"
        flexDirection="column"
        justifyContent="flex-start"
        minHeight={0}
      >
        <Box display="flex" flexDirection="column" flex={1} minHeight={0}>
          {/* Game area — grows with viewport; floor via clamp */}
          <Box
            display="flex"
            flexDirection="column"
            flex="1 1 auto"
            minHeight={0}
            sx={{
              minHeight: {
                xs: "clamp(260px, 52vh, 620px)",
                sm: "clamp(300px, 48vh, 640px)",
              },
              // Avoid stretching the play area across very wide screens.
              width: "100%",
              maxWidth: { xs: "100%", md: "640px" },
              mx: "auto",
            }}
          >
            {children}
          </Box>

          {/* Feedback zone - fixed height so layout doesn't shift */}
          <Box
            height={52}
            display="flex"
            alignItems="center"
            justifyContent="center"
            flexShrink={0}
            sx={{ borderTop: 1, borderColor: "divider" }}
          >
            {feedback ? (
              <Typography
                variant="body1"
                fontWeight={500}
                color={feedback.startsWith("Correct") || feedback.includes("!") ? "primary.main" : "text.secondary"}
              >
                {feedback}
              </Typography>
            ) : (
              <Typography variant="body2" color="text.secondary">
                &nbsp;
              </Typography>
            )}
          </Box>

          {/* Footer: Timer | Score | Mute | Pause - fixed height so layout doesn't shift */}
          <Box
            height={60}
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            px={1}
            flexShrink={0}
            sx={{ borderTop: 1, borderColor: "divider" }}
          >
            <Typography variant="body1" color="text.secondary">
              {timerSeconds != null ? formatTime(timerSeconds) : "—"}
            </Typography>
            <Typography variant="body1" fontWeight={600}>
              {score != null ? t("games.shell.scoreValue", { count: score }) : "—"}
            </Typography>
            <Box display="flex" alignItems="center" gap={0.5}>
              {moreTime ? (
                <IconButton
                  onClick={moreTime.onRequest}
                  disabled={moreTime.disabled === true}
                  aria-label={t("games.shell.moreTime")}
                  sx={{ minWidth: 52, minHeight: 52 }}
                >
                  <HourglassBottomIcon fontSize="medium" />
                </IconButton>
              ) : null}
              {onMuteChange != null ? (
                <IconButton
                  onClick={() => onMuteChange(!muted)}
                  aria-label={muted ? t("games.shell.unmute") : t("games.shell.mute")}
                  sx={{ minWidth: 52, minHeight: 52 }}
                >
                  {muted ? (
                    <VolumeOffIcon fontSize="medium" />
                  ) : (
                    <VolumeUpIcon fontSize="medium" />
                  )}
                </IconButton>
              ) : null}
              {onVoiceOverChange != null ? (
                <IconButton
                  onClick={() => onVoiceOverChange(!voiceOverEnabled)}
                  aria-label={
                    voiceOverEnabled
                      ? t("games.shell.voiceOverOff")
                      : t("games.shell.voiceOverOn")
                  }
                  color={voiceOverEnabled ? "primary" : "default"}
                  sx={{ minWidth: 52, minHeight: 52 }}
                >
                  {voiceOverEnabled ? (
                    <RecordVoiceOverIcon fontSize="medium" />
                  ) : (
                    <VoiceOverOffIcon fontSize="medium" />
                  )}
                </IconButton>
              ) : null}
              {onPause ? (
                <IconButton
                  onClick={onPause}
                  aria-label={paused ? t("games.shell.resume") : t("games.shell.pause")}
                  sx={{ minWidth: 52, minHeight: 52 }}
                >
                  {paused ? (
                    <PlayArrowIcon fontSize="medium" />
                  ) : (
                    <PauseIcon fontSize="medium" />
                  )}
                </IconButton>
              ) : (
                <Box sx={{ width: 52 }} />
              )}
            </Box>
          </Box>
        </Box>
      </Box>
      </Box>
    </PageWrapper>
  );
};

export default GameScreenShell;
